import { UserProvider, withPageAuthRequired } from '@auth0/nextjs-auth0/client';
import { registerMuiLicense } from '@automata/config-mui-x-license';
import { SnackbarProvider } from '@automata/rexports/notistack';
import {
  AdapterDayjs,
  LocalizationProvider,
} from '@automata/rexports/x-date-pickers-pro';
import { env } from '@automata/utils';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppProps } from 'next/app';
import Head from 'next/head';

import { AblyProvider } from 'components/AblyProvider';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { FeedbackWidget } from 'components/FeedbackWidget';
import { Layout } from 'components/Layout';
import { ModalProvider } from 'contexts/ModalContext';
import { CustomThemeProvider } from 'contexts/ThemeContext';
import { UnleashProvider } from 'contexts/UnleashProvider';
registerMuiLicense();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const AppHead = (): JSX.Element => (
  <Head>
    <title>{env('NEXT_PUBLIC_APP_NAME')}</title>
    <link rel="icon" type="image/png" href="/favicon.png" sizes="16x16" />
  </Head>
);

const StylingProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => (
  <StyledEngineProvider>
    <CustomThemeProvider>
      <CssBaseline />
      {children}
    </CustomThemeProvider>
  </StyledEngineProvider>
);

const App = ({ children }: { children: JSX.Element }): JSX.Element => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} position="top-left" />
      <AppHead />
      <SnackbarProvider
        maxSnack={4}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        preventDuplicate
      >
        <UnleashProvider>
          <ErrorBoundary>
            <StylingProvider>
              <ModalProvider>
                <AblyProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Layout>{children}</Layout>
                    <FeedbackWidget />
                  </LocalizationProvider>
                </AblyProvider>
              </ModalProvider>
            </StylingProvider>
          </ErrorBoundary>
        </UnleashProvider>
      </SnackbarProvider>
    </QueryClientProvider>
  );
};

const AppWithAuth = withPageAuthRequired(App);

const AuthWrap = ({ Component, pageProps }: AppProps) => {
  // ErrorPage is a special case where we don't want to wrap it with Auth
  if (Component.displayName === 'ErrorPage') {
    return (
      <>
        <AppHead />
        <StyledEngineProvider>
          <Component {...pageProps} />
        </StyledEngineProvider>
      </>
    );
  }
  return (
    <UserProvider>
      <AppWithAuth>
        <Component {...pageProps} />
      </AppWithAuth>
    </UserProvider>
  );
};

export default AuthWrap;
